import { useCallback, useState } from 'react';
import type { DocumentViewerDocument } from './types';

export function useDocumentViewer(documents: DocumentViewerDocument[]) {
  const [selectedDocumentId, setSelectedDocumentId] = useState<string | null>(null);
  const viewableDocuments = documents;
  const selectedIndex = documents.findIndex(({ id }) => id === selectedDocumentId);
  const selectedDocument = documents[selectedIndex] ?? null;

  const canNavigatePrev = selectedIndex > 0;
  const canNavigateNext = selectedIndex >= 0 && selectedIndex < viewableDocuments.length - 1;

  const navigateNext = useCallback(() => {
    if (canNavigateNext) {
      const nextDoc = viewableDocuments[selectedIndex + 1];
      setSelectedDocumentId(nextDoc.id);
    }
  }, [selectedIndex, viewableDocuments, canNavigateNext]);

  const navigatePrevious = useCallback(() => {
    if (canNavigatePrev) {
      const prevDoc = viewableDocuments[selectedIndex - 1];
      setSelectedDocumentId(prevDoc.id);
    }
  }, [selectedIndex, viewableDocuments, canNavigatePrev]);

  const selectDocument = useCallback(
    (document: DocumentViewerDocument) => {
      if (viewableDocuments.some(({ id }) => id === document.id)) {
        setSelectedDocumentId(document.id);
      }
    },
    [viewableDocuments],
  );

  const closeViewer = useCallback(() => {
    setSelectedDocumentId(null);
  }, []);

  return {
    selectedDocument,
    selectedIndex,
    viewableDocuments,
    canNavigatePrev,
    canNavigateNext,
    navigateNext,
    navigatePrevious,
    selectDocument,
    closeViewer,
  };
}
